<template>
  <b-modal
    id="downloads-modal"
    ok-only
    scrollable
    centered
    ok-title="Esci"
    title="I miei download"
    size="xl"
    @show="onShow"
    @hidden="onHidden"
  >
    <MyLoading :loading-show="loadingShow" />

    <p class="mx-md-3 mx-sm-1 mb-0 text-muted">
      {{ MODAL_INTRO_TEXT }}
    </p>

    <div class="mx-md-3 mx-sm-1 py-1 d-flex align-items-center justify-content-between">
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        style="border: none"
        variant="outline-primary"
        aria-label="Aggiorna l'elenco dei download"
        :disabled="loadingShow"
        @click="refresh"
      >
        <font-awesome-icon
          icon="arrows-rotate"
          size="lg"
          class="mr-50"
        />
        Aggiorna
      </b-button>
      <small
        v-if="autoRefreshSuspended"
        class="text-muted ml-1"
      >{{ AUTO_REFRESH_SUSPENDED_TEXT }}</small>
    </div>

    <div class="mx-md-3 mx-sm-1">
      <b-table
        :items="tableRows"
        :fields="fields"
        primary-key="id"
        responsive
        hover
        show-empty
        :per-page="perPage"
        :current-page="currentPage"
      >
        <template #cell(created_at)="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template #cell(process_state)="{ item }">
          <b-badge :variant="item.stateView.variant">
            <b-spinner
              v-if="item.stateView.spinner"
              small
              class="mr-25"
              aria-hidden="true"
            />
            {{ item.stateView.label }}
          </b-badge>
          <small
            v-if="item.stateView.note"
            class="d-block text-muted mt-25"
          >{{ item.stateView.note }}</small>
        </template>

        <template #cell(archive_name)="{ item }">
          {{ item.archive_name || MISSING_VALUE }}
        </template>

        <template #cell(archive_size_bytes)="{ item }">
          {{ formatSize(item.archive_size_bytes) }}
        </template>

        <template #cell(actions)="{ item }">
          <b-button
            v-if="item.stateView.canCancel"
            size="sm"
            variant="outline-danger"
            :aria-label="cancelAriaLabel(item)"
            :disabled="cancellingIds.includes(item.id)"
            @click="requestCancel(item)"
          >
            Annulla
          </b-button>
          <b-button
            v-if="item.stateView.canDownload"
            size="sm"
            variant="outline-primary"
            :aria-label="downloadAriaLabel(item)"
            :disabled="downloadingIds.includes(item.id)"
            @click="requestDownload(item)"
          >
            Scarica
          </b-button>
        </template>

        <template #empty>
          <div class="text-center py-3">
            <p class="font-weight-bolder mb-50">
              {{ EMPTY_TITLE }}
            </p>
            <p class="text-muted mb-0">
              {{ EMPTY_TEXT }}
            </p>
          </div>
        </template>
      </b-table>

      <b-pagination
        v-if="rows.length > perPage"
        v-model="currentPage"
        :per-page="perPage"
        :total-rows="rows.length"
        size="sm"
        align="center"
        class="mt-1"
      />
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BTable, BBadge, BSpinner, BButton, BPagination,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import bulkDownloadRepository from '@/api/bulkDownloadRepository'
import bulkDownload from '@/helpers/bulk-download'
import MyLoading from '@/layouts/components/MyLoading.vue'

export default {
  name: 'DownloadsModal',
  directives: {
    Ripple,
  },
  components: {
    MyLoading,
    BModal,
    BTable,
    BBadge,
    BSpinner,
    BButton,
    BPagination,
  },
  data() {
    return {
      rows: [],
      loadingShow: false,
      visible: false,
      loadSeq: 0,
      cancellingIds: [],
      downloadingIds: [],
      pendingCancelIds: [],
      pendingCancelAt: {},
      autoRefreshSuspended: false,
      currentPage: 1,
      perPage: bulkDownload.PAGE_SIZE,
      fields: [
        { key: 'created_at', label: 'Richiesto il', tdClass: 'text-nowrap' },
        { key: 'process_state', label: 'Stato' },
        { key: 'archive_name', label: 'Archivio', tdClass: 'text-break' },
        {
          key: 'archive_size_bytes', label: 'Dimensione', thClass: 'text-right', tdClass: 'text-right text-nowrap',
        },
        { key: 'actions', label: 'Azioni', tdClass: 'text-nowrap' },
      ],
      MODAL_INTRO_TEXT: bulkDownload.MODAL_INTRO_TEXT,
      AUTO_REFRESH_SUSPENDED_TEXT: bulkDownload.AUTO_REFRESH_SUSPENDED_TEXT,
      MISSING_VALUE: bulkDownload.MISSING_VALUE,
      EMPTY_TITLE: bulkDownload.EMPTY_TITLE,
      EMPTY_TEXT: bulkDownload.EMPTY_TEXT,
    }
  },
  computed: {
    tableRows() {
      return bulkDownload.sortRows(this.rows).map(row => ({ ...row, stateView: bulkDownload.getStateView(row) }))
    },
  },
  created() {
    this.poller = bulkDownload.createPoller({
      fetchRows: () => this.fetchRowsSilently(),
      onRows: rows => this.applyRows(rows),
      onError: (error, info) => this.onPollError(error, info),
    })
    this.downloadFrames = {}
    this.downloadRefetch = bulkDownload.createDownloadRefetch({ onSettle: id => this.onDownloadSettled(id) })
  },
  beforeDestroy() {
    this.poller.stop()
    this.downloadRefetch.cancelAll().forEach(id => this.releaseDownload(id))
  },
  methods: {
    onShow() {
      this.visible = true
      this.pendingCancelIds = []
      this.pendingCancelAt = {}
      this.autoRefreshSuspended = false
      this.currentPage = 1
      this.load()
    },
    onHidden() {
      this.visible = false
      this.loadSeq += 1
      this.poller.stop()
      this.downloadRefetch.cancelAll().forEach(id => this.releaseDownload(id))
    },
    refresh() {
      this.autoRefreshSuspended = false
      this.load()
    },
    async fetchRowsSilently() {
      const response = await bulkDownloadRepository.list()
      const rows = bulkDownload.readListRows(response)
      if (rows === null) {
        throw response
      }
      return rows
    },
    applyRows(rows) {
      const livePending = bulkDownload.expirePendingCancels(this.pendingCancelIds, this.pendingCancelAt)
      const merged = bulkDownload.mergeList(rows, livePending)
      this.rows = merged.rows
      this.pendingCancelIds = merged.pendingCancelIds
      const pages = Math.max(1, Math.ceil(this.rows.length / this.perPage))
      if (this.currentPage > pages) {
        this.currentPage = pages
      }
    },
    onPollError(error, info) {
      if (!info.unauthorized && info.failures === 1) {
        this.apiErrors.notifyApiError(error)
      }
      if (info.suspended && !info.unauthorized) {
        this.autoRefreshSuspended = true
      }
    },
    async load({ silent = false } = {}) {
      this.poller.stop()
      this.loadSeq += 1
      const seq = this.loadSeq
      if (!silent) {
        this.loadingShow = true
      }
      try {
        const response = await bulkDownloadRepository.list()
        if (seq !== this.loadSeq) {
          return
        }
        const rows = bulkDownload.readListRows(response)
        if (Array.isArray(rows)) {
          this.applyRows(rows)
          this.autoRefreshSuspended = false
          if (this.visible && bulkDownload.hasActive(this.rows)) {
            this.poller.ensureRunning()
          }
        } else {
          this.apiErrors.notifyApiError(response)
          this.autoRefreshSuspended = this.visible && bulkDownload.hasActive(this.rows)
        }
      } catch (e) {
        if (seq === this.loadSeq) {
          this.apiErrors.notifyApiError(e)
          this.autoRefreshSuspended = this.visible && bulkDownload.hasActive(this.rows)
        }
      } finally {
        if (seq === this.loadSeq || !this.visible) {
          this.loadingShow = false
        }
      }
    },
    cancelAriaLabel(item) {
      return `Annulla il download richiesto il ${this.formatDate(item.created_at)}`
    },
    downloadAriaLabel(item) {
      return `Scarica l'archivio richiesto il ${this.formatDate(item.created_at)}`
    },
    releaseDownload(id) {
      this.downloadingIds = this.downloadingIds.filter(x => x !== id)
    },
    async onDownloadSettled(id) {
      try {
        if (this.visible) {
          await this.load({ silent: true })
        }
      } finally {
        this.releaseDownload(id)
      }
    },
    async requestDownload(row) {
      if (!row || row.id === null || row.id === undefined || !bulkDownload.getStateView(row).canDownload) {
        return
      }
      if (this.downloadingIds.includes(row.id)) {
        return
      }
      this.downloadingIds = [...this.downloadingIds, row.id]
      let started = false
      try {
        const response = await bulkDownloadRepository.getLink(row.id)
        const link = bulkDownload.readDownloadLink(response)
        if (link === null) {
          this.apiErrors.notifyApiError(response, bulkDownload.DOWNLOAD_ERROR_OVERRIDES)
          await this.load({ silent: true })
          return
        }
        this.downloadFrames[row.id] = bulkDownload.startNativeDownload(link, this.downloadFrames[row.id] || null)
        started = true
        this.downloadRefetch.schedule(row.id)
      } catch (e) {
        this.apiErrors.notifyApiError(e, bulkDownload.DOWNLOAD_ERROR_OVERRIDES)
        await this.load({ silent: true })
      } finally {
        if (!started) {
          this.releaseDownload(row.id)
        }
      }
    },
    async requestCancel(row) {
      if (!row || row.id === null || row.id === undefined || !bulkDownload.getStateView(row).canCancel) {
        return
      }
      if (this.cancellingIds.includes(row.id)) {
        return
      }
      this.cancellingIds = [...this.cancellingIds, row.id]
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(bulkDownload.CANCEL_CONFIRM.body, {
          title: bulkDownload.CANCEL_CONFIRM.title,
          okTitle: bulkDownload.CANCEL_CONFIRM.okTitle,
          cancelTitle: bulkDownload.CANCEL_CONFIRM.cancelTitle,
          okVariant: 'danger',
          cancelVariant: 'outline-secondary',
          centered: true,
        })
        if (confirmed !== true) {
          return
        }
        const current = this.rows.find(r => r.id === row.id)
        if (!current || !bulkDownload.getStateView(current).canCancel) {
          return
        }
        const response = await bulkDownloadRepository.cancel(row.id)
        if (response && response.data && Number(response.data.code) === 1) {
          if (!this.pendingCancelIds.includes(row.id)) {
            this.pendingCancelIds = [...this.pendingCancelIds, row.id]
          }
          this.pendingCancelAt = { ...this.pendingCancelAt, [row.id]: Date.now() }
          this.rows = bulkDownload.markCancelRequested(this.rows, row.id)
          if (this.visible && bulkDownload.hasActive(this.rows)) {
            this.poller.ensureRunning()
          }
        } else {
          this.apiErrors.notifyApiError(response, bulkDownload.CANCEL_ERROR_OVERRIDES)
          await this.load({ silent: true })
        }
      } catch (e) {
        this.apiErrors.notifyApiError(e, bulkDownload.CANCEL_ERROR_OVERRIDES)
        await this.load({ silent: true })
      } finally {
        this.cancellingIds = this.cancellingIds.filter(id => id !== row.id)
      }
    },
    formatDate(value) {
      return this.dateHelpers.format(value, bulkDownload.DATE_FORMAT)
    },
    formatSize(bytes) {
      return bulkDownload.formatBytes(bytes)
    },
  },
}
</script>
